import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import client, { mensajeDeError } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import Spinner from '../components/ui/Spinner';
import { MensajeError } from '../components/ui/Form';
import { Contenedor, TituloPagina, Subtitulo } from '../components/layout/Layout';

const Lista = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.space.lg};
  grid-template-columns: 1fr;

  @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const Paquete = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  text-align: center;

  h2 {
    font-size: 1.2rem;
  }

  p {
    font-size: 0.88rem;
    color: ${({ theme }) => theme.colors.textMuted};
    flex: 1;
  }
`;

const Precio = styled.p`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 2rem;
  color: ${({ theme }) => theme.colors.sageDeep};

  span {
    font-size: 0.9rem;
    font-family: ${({ theme }) => theme.fonts.body};
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const Politica = styled(Card)`
  margin-top: ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => theme.colors.warningLight};
  border-color: transparent;

  h2 {
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.warning};
    margin-bottom: ${({ theme }) => theme.space.sm};
  }

  p {
    font-size: 0.92rem;
    color: ${({ theme }) => theme.colors.text};
  }
`;

const DetalleCompra = styled.div`
  background: ${({ theme }) => theme.colors.sand};
  border-radius: ${({ theme }) => theme.radii.sm};
  padding: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.md};

  p {
    font-size: 0.95rem;
  }

  strong {
    color: ${({ theme }) => theme.colors.sageDeep};
  }
`;

const Aviso = styled.p`
  font-size: 0.9rem;
  margin-bottom: ${({ theme }) => theme.space.md};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Acciones = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.sm};
  justify-content: flex-end;
`;

function formatoPrecio(precio) {
  return `$${Number(precio).toLocaleString('es-MX', { minimumFractionDigits: 0 })}`;
}

function Paquetes() {
  const { usuario, esStaff } = useAuth();
  const navigate = useNavigate();

  const [paquetes, setPaquetes] = useState(null);
  const [paqueteActual, setPaqueteActual] = useState(null);
  const [porComprar, setPorComprar] = useState(null);
  const [comprando, setComprando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    client.get('/paquetes').then(({ data }) => setPaquetes(data)).catch(() => setPaquetes([]));
  }, []);

  useEffect(() => {
    if (usuario && !esStaff) {
      client.get('/paquetes/mio').then(({ data }) => setPaqueteActual(data)).catch(() => {});
    }
  }, [usuario, esStaff]);

  const alComprar = (p) => {
    if (!usuario) {
      navigate('/login', { state: { desde: '/paquetes' } });
      return;
    }
    setError('');
    setPorComprar(p);
  };

  const confirmarCompra = async () => {
    setComprando(true);
    setError('');
    try {
      await client.post(`/paquetes/${porComprar.id}/comprar`);
      navigate('/mi-cuenta/paquete');
    } catch (err) {
      setError(mensajeDeError(err));
    } finally {
      setComprando(false);
    }
  };

  if (!paquetes) return <Spinner />;

  return (
    <Contenedor>
      <TituloPagina>Paquetes y precios</TituloPagina>
      <Subtitulo>
        Elige el ritmo que va contigo: desde una clase suelta para conocernos hasta la membresía
        ilimitada. Actívalo en línea y reserva de inmediato.
      </Subtitulo>
      {error && !porComprar && <MensajeError role="alert">{error}</MensajeError>}
      <Lista>
        {paquetes.map((p) => (
          <Paquete key={p.id}>
            <h2>{p.nombre}</h2>
            <Precio>
              {formatoPrecio(p.precio)} <span>MXN</span>
            </Precio>
            <Badge $tono={p.tipo === 'ilimitado' ? 'sage' : 'arena'}>
              {p.tipo === 'ilimitado' ? 'Clases ilimitadas' : `${p.numero_clases} clase${p.numero_clases > 1 ? 's' : ''}`}
            </Badge>
            <Badge $tono="arena">Vigencia {p.dias_vigencia} días</Badge>
            <p>{p.descripcion}</p>
            {!esStaff && (
              <Button $chico onClick={() => alComprar(p)}>
                Adquirir
              </Button>
            )}
          </Paquete>
        ))}
      </Lista>
      <Politica>
        <h2>Política de cancelación</h2>
        <p>
          Puedes cancelar tu reserva sin costo hasta <strong>12 horas antes</strong> de la clase y
          la sesión regresa a tu paquete. Si cancelas con menos de 12 horas de anticipación, la
          clase se descuenta de tu paquete.
        </p>
      </Politica>

      <Modal
        abierto={!!porComprar}
        titulo="Confirmar paquete"
        onCerrar={() => setPorComprar(null)}
      >
        {porComprar && (
          <>
            <DetalleCompra>
              <p>
                <strong>{porComprar.nombre}</strong> · {formatoPrecio(porComprar.precio)} MXN
              </p>
              <p>
                {porComprar.tipo === 'ilimitado'
                  ? 'Clases ilimitadas'
                  : `${porComprar.numero_clases} clases`}{' '}
                · vigencia de {porComprar.dias_vigencia} días a partir de hoy
              </p>
            </DetalleCompra>
            {paqueteActual && (
              <Aviso>
                Ya tienes un paquete activo (<strong>{paqueteActual.paquete_nombre}</strong>
                {paqueteActual.tipo === 'por_clases'
                  ? `, ${paqueteActual.clases_restantes} clases restantes`
                  : ''}
                ). Al adquirir uno nuevo, el actual se reemplaza y sus clases no se conservan.
              </Aviso>
            )}
            <Aviso>El pago se realiza en recepción; tu paquete queda activo desde ahora.</Aviso>
            {error && <MensajeError role="alert">{error}</MensajeError>}
            <Acciones>
              <Button $variante="fantasma" onClick={() => setPorComprar(null)}>
                Volver
              </Button>
              <Button onClick={confirmarCompra} disabled={comprando}>
                {comprando ? 'Activando…' : 'Confirmar'}
              </Button>
            </Acciones>
          </>
        )}
      </Modal>
    </Contenedor>
  );
}

export default Paquetes;
